import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertTriangle, ShieldAlert, Power, KeyRound, Pencil, Loader2, Route,
  Building2, CheckCircle2, Clock3, ExternalLink,
} from 'lucide-react';
import {
  automationService,
  type PaperTradingHealth,
  type PaperExecutionMode,
  type PaperTradingSettings as PaperSettings,
  type PaperTradingSettingsUpdate,
  type SystemPaperTradingSettings,
} from '@/services/strategyService';
import { useAuth } from '@/auth/AuthContext';
import {
  brokerSelectionService,
  type BrokerIntegrationCatalogEntry,
} from '@/services/brokerSelectionService';

// ── Helpers ──────────────────────────────────────────────────────

function maskApiKey(key: string | null | undefined): string {
  if (!key) return '—';
  if (key.length <= 12) return key;
  return `${key.slice(0, 8)}…${key.slice(-4)}`;
}

function relTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (isNaN(t)) return iso || '—';
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 30) return `${Math.round(diffSec / 86400)}d ago`;
  return new Date(t).toLocaleDateString();
}

function isBrokerReachable(h: PaperTradingHealth | null): boolean {
  if (!h) return false;
  if (typeof h.brokerReachable === 'boolean') return h.brokerReachable;
  if (typeof h.accountReachable === 'boolean') return h.accountReachable;
  return false;
}

// ── Page ─────────────────────────────────────────────────────────

/**
 * Configuration → Paper Trading detail panel.
 * Owns the broker-credentials editor + the master kill switch.
 * Per-rule on/off list lives on PaperTradingPage (active-trading concern).
 */
export const PaperTradingSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.roles?.includes('SuperAdmin');

  const [settings, setSettings] = useState<PaperSettings | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [health, setHealth] = useState<PaperTradingHealth | null>(null);

  const [pendingMasterToggle, setPendingMasterToggle] = useState<boolean>(false);
  const [confirmEnableOpen, setConfirmEnableOpen] = useState(false);
  const [masterToggling, setMasterToggling] = useState(false);
  const [controlError, setControlError] = useState<string | null>(null);

  // SuperAdmin-only: system master switch state. Loaded only when the
  // current user is an admin — non-admins get 403 from the backend, so
  // there's no point hitting the endpoint.
  const [systemSettings, setSystemSettings] = useState<SystemPaperTradingSettings | null>(null);
  const [pendingSystemToggle, setPendingSystemToggle] = useState<boolean>(false);
  const [confirmSystemEnableOpen, setConfirmSystemEnableOpen] = useState(false);
  const [systemToggling, setSystemToggling] = useState(false);
  const [systemError, setSystemError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const tasks: Promise<any>[] = [
      automationService.getPaperSettings(),
      automationService.getPaperHealth(),
    ];
    if (isSuperAdmin) {
      tasks.push(automationService.getSystemPaperSettings());
    }
    const results = await Promise.allSettled(tasks);
    const [s, h, sys] = results;
    if (s.status === 'fulfilled') {
      setSettings(s.value);
      setSettingsLoaded(true);
    }
    if (h.status === 'fulfilled') setHealth(h.value);
    if (isSuperAdmin && sys && sys.status === 'fulfilled') {
      setSystemSettings(sys.value);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const requestSystemToggle = (next: boolean) => {
    setSystemError(null);
    if (next) {
      setPendingSystemToggle(true);
      setConfirmSystemEnableOpen(true);
    } else {
      void applySystemToggle(false);
    }
  };

  const applySystemToggle = async (next: boolean) => {
    setSystemToggling(true);
    setSystemError(null);
    try {
      const updated = await automationService.setSystemPaperSettings(next);
      setSystemSettings(updated);
      // Per-user settings response carries the master flag too — refresh so
      // the banner below disappears/appears immediately.
      try {
        const s = await automationService.getPaperSettings();
        setSettings(s);
      } catch { /* non-fatal */ }
    } catch (e: any) {
      setSystemError(e?.message || 'Failed to update master switch');
    } finally {
      setSystemToggling(false);
      setConfirmSystemEnableOpen(false);
      setPendingSystemToggle(false);
    }
  };

  const requestMasterToggle = (next: boolean) => {
    setControlError(null);
    if (next) {
      setPendingMasterToggle(true);
      setConfirmEnableOpen(true);
    } else {
      void applyMasterToggle(false);
    }
  };

  const applyMasterToggle = async (next: boolean) => {
    setMasterToggling(true);
    setControlError(null);
    try {
      const updated = await automationService.savePaperSettings({ paperTradingEnabled: next });
      setSettings(updated);
      try {
        const h = await automationService.getPaperHealth();
        setHealth(h);
      } catch { /* non-fatal */ }
    } catch (e: any) {
      setControlError(e?.message || 'Failed to update master switch');
    } finally {
      setMasterToggling(false);
      setConfirmEnableOpen(false);
      setPendingMasterToggle(false);
    }
  };

  const saveCredentials = async (update: PaperTradingSettingsUpdate) => {
    const next = await automationService.savePaperSettings(update);
    setSettings(next);
    setSettingsLoaded(true);
    try {
      const h = await automationService.getPaperHealth();
      setHealth(h);
    } catch { /* non-fatal */ }
    return next;
  };

  const saveExecutionModes = async (update: PaperTradingSettingsUpdate) => {
    const next = await automationService.savePaperSettings(update);
    setSettings(next);
    return next;
  };

  const brokerReachable = isBrokerReachable(health);
  const accountNumber = health?.accountNumber || null;
  const enabled = !!settings?.paperTradingEnabled;
  const masterDisabled = settings == null || masterToggling || !brokerReachable;

  // Fully-simulated = both asset classes on in-process sim → no broker account exists,
  // so the "real orders against account X" copy would be misleading.
  const allSim = (settings?.equityExecutionMode ?? 'Broker') === 'Simulated'
    && (settings?.forexExecutionMode ?? 'Simulated') === 'Simulated';

  const oneLiner = settings == null
    ? 'Loading paper trading state…'
    : !brokerReachable
      ? 'Set your broker credentials below first.'
      : enabled
        ? (allSim
            ? 'In-process simulation — strategies run synthetically with no broker orders.'
            : `Rules placing real orders against paper account ${accountNumber ?? '(unknown)'}.`)
        : 'Real orders are suppressed. Rules still simulate in Marten.';

  // Master-off banner is SuperAdmin-only — non-admin users shouldn't even know
  // a system-wide gate exists; they manage their own opt-in and trust that
  // operators handle system-level concerns.
  const masterOff = isSuperAdmin && settings != null && settings.masterEnabled === false;
  const masterOffBanner = 'Master switch is OFF. Flip it on above to allow paper trading. Your own toggle below also needs to be on for your trades to fire.';

  // SuperAdmin card metadata.
  const sysEnabled = !!systemSettings?.masterEnabled;
  const sysShortUser = systemSettings?.updatedByUserId
    ? systemSettings.updatedByUserId.slice(0, 8)
    : null;

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">Paper Trading</h2>
        <p className="text-xs text-muted-foreground">
          Connect your broker account and enable paper-trading orders.
        </p>
      </div>

      {/* SuperAdmin: System Master Switch (controls every user) */}
      {isSuperAdmin && (
        <Card className={sysEnabled ? '' : 'border-amber-500/40'}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-500" />
              System Master Switch
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <div className="rounded-md border bg-card/50 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">System-Wide Paper Trading</span>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        sysEnabled
                          ? 'bg-emerald-500/15 text-emerald-500'
                          : 'bg-red-500/15 text-red-500'
                      }`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${sysEnabled ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      {sysEnabled ? 'ENABLED' : 'DISABLED'}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Pauses paper trading for <span className="font-semibold">every user</span> on this system.
                    Use this as a global circuit breaker. Individual users' kill-switches still apply on top of this.
                  </div>
                  {systemSettings && (
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Last changed by{' '}
                      <span className="font-mono text-foreground">{sysShortUser ?? '—'}</span>{' '}
                      {systemSettings.updatedAt ? `· ${relTime(systemSettings.updatedAt)}` : ''}
                    </div>
                  )}
                </div>
                <ToggleSwitch
                  checked={sysEnabled}
                  disabled={systemSettings == null || systemToggling}
                  loading={systemToggling}
                  onChange={(next) => requestSystemToggle(next)}
                  ariaLabel="Toggle system master paper trading switch"
                  size="lg"
                />
              </div>
              {systemError && (
                <div className="text-xs text-red-500 mt-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {systemError}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Master kill switch (per-user) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Power className="h-4 w-4 text-primary" />
            Paper Trading (my account)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          {masterOff && (
            <div className="text-xs text-amber-600 dark:text-amber-400 p-3 border border-amber-500/30 rounded-md bg-amber-500/5 flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{masterOffBanner}</span>
            </div>
          )}
          <div className="rounded-md border bg-card/50 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Paper Trading</span>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      enabled
                        ? 'bg-emerald-500/15 text-emerald-500'
                        : 'bg-red-500/15 text-red-500'
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${enabled ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    {enabled ? 'ENABLED' : 'DISABLED'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{oneLiner}</div>
              </div>
              <ToggleSwitch
                checked={enabled}
                disabled={masterDisabled}
                loading={masterToggling}
                onChange={(next) => requestMasterToggle(next)}
                ariaLabel="Toggle master paper trading switch"
                size="lg"
              />
            </div>
            {controlError && (
              <div className="text-xs text-red-500 mt-2 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> {controlError}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Execution source (sim vs real paper/demo broker) */}
      <ExecutionSourceCard settings={settings} onSave={saveExecutionModes} />

      {/* US broker coverage and rollout status */}
      <BrokerCoverageCard />

      {/* Alpaca paper credentials */}
      <BrokerCredentialsCard
        settings={settings}
        settingsLoaded={settingsLoaded}
        onSave={saveCredentials}
      />

      {/* Confirm dialog */}
      <Dialog
        open={confirmEnableOpen}
        onOpenChange={(open) => {
          if (!open && !masterToggling) {
            setConfirmEnableOpen(false);
            setPendingMasterToggle(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Enable Paper Trading?
            </DialogTitle>
            <DialogDescription>
              Live paper orders will start firing for any enabled rule. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              disabled={masterToggling}
              onClick={() => {
                setConfirmEnableOpen(false);
                setPendingMasterToggle(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={masterToggling}
              onClick={() => void applyMasterToggle(pendingMasterToggle)}
            >
              {masterToggling ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Yes, enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SuperAdmin: confirm dialog for the system-wide master switch.
          Only triggered when turning ON — turning OFF is a safety action,
          no confirmation needed. */}
      <Dialog
        open={confirmSystemEnableOpen}
        onOpenChange={(open) => {
          if (!open && !systemToggling) {
            setConfirmSystemEnableOpen(false);
            setPendingSystemToggle(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Enable System-Wide Paper Trading?
            </DialogTitle>
            <DialogDescription>
              Live paper orders will start firing for any user who has BOTH master ON and their own toggle ON. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              disabled={systemToggling}
              onClick={() => {
                setConfirmSystemEnableOpen(false);
                setPendingSystemToggle(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={systemToggling}
              onClick={() => void applySystemToggle(pendingSystemToggle)}
            >
              {systemToggling ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Yes, enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Broker Coverage Card ────────────────────────────────────────

const accessLabel: Record<BrokerIntegrationCatalogEntry['accessStatus'], string> = {
  'public-direct': 'Public direct API',
  'approval-required': 'Broker approval required',
  'commercial-required': 'Commercial approval required',
  'commercial-discovery': 'Official API access unconfirmed',
  'terminal-agent': 'Broker terminal agent',
};

function configurationHref(tileId: string): string {
  const queryPart = window.location.hash.includes('?')
    ? `?${window.location.hash.split('?')[1]}`
    : '';
  return `#/dashboard/configuration/${tileId}${queryPart}`;
}

const BrokerCoverageCard: React.FC = () => {
  const [catalog, setCatalog] = useState<BrokerIntegrationCatalogEntry[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void brokerSelectionService.getCatalog()
      .then((entries) => {
        if (!active) return;
        setCatalog(entries);
        setError(null);
      })
      .catch((e: any) => {
        if (!active) return;
        setError(e?.message || 'Failed to load broker catalog');
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => { active = false; };
  }, []);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" />
          Direct Broker Integration Program
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <p className="text-xs text-muted-foreground max-w-3xl">
            The program tracks 15 direct broker families plus cTrader, MetaTrader 5, and MetaTrader 4 as
            approved broker-native execution platforms. No independent aggregation platform will receive
            credentials or route orders. Stake and Wealthsimple remain blocked until they provide an official
            direct commercial API specification; legacy MetaTrader 3 is not a supported target.
          </p>
          <a
            href={configurationHref('broker-routing')}
            className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-background px-3 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
          >
            Broker routing <ExternalLink className="ml-1 h-3 w-3" />
          </a>
        </div>

        {!loaded && (
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading broker catalog…
          </div>
        )}

        {error && (
          <div className="text-xs text-red-500 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> {error}
          </div>
        )}

        {loaded && !error && (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full min-w-[760px] text-left text-xs">
              <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Broker</th>
                  <th className="px-3 py-2 font-medium">Connector</th>
                  <th className="px-3 py-2 font-medium">Paper / live</th>
                  <th className="px-3 py-2 font-medium">Product status</th>
                </tr>
              </thead>
              <tbody>
                {catalog.map((broker) => {
                  const available = broker.implementationStatus === 'available';
                  const blocked = broker.implementationStatus === 'blocked';
                  // 'preview' means an adapter is registered but has NOT been certified
                  // against the broker's own environment - it must never read as ready.
                  const preview = broker.implementationStatus === 'preview';
                  return (
                    <tr key={broker.code} className="border-t align-top">
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-foreground">
                          <span className="mr-2 text-muted-foreground">{broker.rank}</span>
                          {broker.displayName}
                        </div>
                        <div className="mt-0.5 text-[10px] text-muted-foreground">
                          {broker.credentialFlow}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div>{broker.connector}</div>
                        <div className="mt-1 inline-flex rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                          {accessLabel[broker.accessStatus]}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">{broker.paperTrading}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold ${
                          available
                            ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                            : blocked
                              ? 'bg-red-500/15 text-red-600 dark:text-red-400'
                              : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                        }`}>
                          {available
                            ? <CheckCircle2 className="h-3 w-3" />
                            : blocked
                              ? <AlertTriangle className="h-3 w-3" />
                              : <Clock3 className="h-3 w-3" />}
                          {available
                            ? 'Ready'
                            : preview
                              ? 'Preview - not certified'
                              : blocked
                                ? 'Direct API access blocked'
                                : 'Adapter not installed'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground">
          “Ready” is derived from the running broker registry. Planned or blocked entries cannot be selected and cannot
          receive orders. Existing IG and FXCM routes remain available for CFD/forex trading and are outside this discovery set.
        </p>
      </CardContent>
    </Card>
  );
};

// ── Execution Source Card ───────────────────────────────────────

interface ExecutionSourceCardProps {
  settings: PaperSettings | null;
  onSave: (update: PaperTradingSettingsUpdate) => Promise<PaperSettings>;
}

/**
 * Lets the user choose, per asset class, whether paper trades run as an
 * in-process simulation or are routed to a real paper/demo brokerage account
 * (so strategies trade under real spreads/fills).
 *   • Equity  Broker → Alpaca paper account (the long-standing default).
 *   • Forex   Broker → FXCM *demo* account (needs demo FXCM credentials).
 */
const ExecutionSourceCard: React.FC<ExecutionSourceCardProps> = ({ settings, onSave }) => {
  const [savingKey, setSavingKey] = useState<'equity' | 'forex' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const equityMode: PaperExecutionMode = settings?.equityExecutionMode ?? 'Broker';
  const forexMode: PaperExecutionMode = settings?.forexExecutionMode ?? 'Simulated';

  const save = async (which: 'equity' | 'forex', mode: PaperExecutionMode) => {
    setError(null);
    setSavingKey(which);
    try {
      await onSave(
        which === 'equity' ? { equityExecutionMode: mode } : { forexExecutionMode: mode },
      );
    } catch (e: any) {
      setError(e?.message || 'Failed to update execution source');
    } finally {
      setSavingKey(null);
    }
  };

  const Row: React.FC<{
    label: string;
    desc: string;
    which: 'equity' | 'forex';
    value: PaperExecutionMode;
    brokerHint: string;
  }> = ({ label, desc, which, value, brokerHint }) => (
    <div className="rounded-md border bg-card/50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">{label}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
          {value === 'Broker' && (
            <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-1 flex items-start gap-1">
              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" /> {brokerHint}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {savingKey === which && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          <select
            value={value}
            disabled={settings == null || savingKey === which}
            onChange={(e) => void save(which, e.target.value as PaperExecutionMode)}
            className="h-8 rounded-md border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            <option value="Simulated">Internal simulation</option>
            <option value="Broker">Broker account</option>
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Route className="h-4 w-4 text-primary" />
          Execution Source
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <p className="text-xs text-muted-foreground">
          Choose where paper orders execute. <span className="font-medium">Internal simulation</span> fills
          synthetically in-process; <span className="font-medium">Broker account</span> routes orders to a real
          paper/demo brokerage so strategies trade under real spreads and fills.
        </p>
        <p className="text-xs text-amber-500/90">
          Each source keeps its own positions. Anything open in the other mode is hidden (not lost)
          until you switch back — flatten first if you're switching for good.
        </p>
        <Row
          label="Equities"
          desc="Stock paper trades."
          which="equity"
          value={equityMode}
          brokerHint="Routes to your Alpaca paper account (set credentials below)."
        />
        <Row
          label="Forex"
          desc="Currency paper trades."
          which="forex"
          value={forexMode}
          brokerHint="Routes to an FXCM demo account — requires demo FXCM credentials. Experimental; verify on the demo account before relying on it."
        />
        {error && (
          <div className="text-xs text-red-500 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Broker Credentials Card ─────────────────────────────────────

interface BrokerCredentialsCardProps {
  settings: PaperSettings | null;
  settingsLoaded: boolean;
  onSave: (update: PaperTradingSettingsUpdate) => Promise<PaperSettings>;
}

const BrokerCredentialsCard: React.FC<BrokerCredentialsCardProps> = ({
  settings,
  settingsLoaded,
  onSave,
}) => {
  const hasCreds = !!(settings && settings.apiKey);
  const [editing, setEditing] = useState(!hasCreds);
  const [apiKey, setApiKey] = useState<string>(settings?.apiKey || '');
  const [apiSecret, setApiSecret] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setApiKey(settings.apiKey || '');
    }
    if (settingsLoaded && !settings) setEditing(true);
  }, [settings, settingsLoaded]);

  const handleCancel = () => {
    setError(null);
    setApiSecret('');
    if (settings) {
      setApiKey(settings.apiKey || '');
      setEditing(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const update: PaperTradingSettingsUpdate = {
        provider: 'Alpaca',
        apiKey,
      };
      if (apiSecret.length > 0) update.apiSecret = apiSecret;
      await onSave(update);
      setApiSecret('');
      setEditing(false);
    } catch (e: any) {
      setError(e?.message || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const emptyState = settingsLoaded && !hasCreds;

  return (
    <Card className={emptyState ? 'border-amber-500/40' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" />
            Alpaca Paper Credentials
          </span>
          {hasCreds && !editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="h-3.5 w-3.5 mr-1" />
              Edit
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {emptyState && !editing && (
          <div className="text-xs text-amber-500 p-3 border border-amber-500/30 rounded-md bg-amber-500/5">
            Add your Alpaca paper-trading API key to place broker-backed paper orders.
            We never display your secret after saving.
          </div>
        )}

        {!editing && hasCreds && settings && (
          <div className="text-xs">
            <span className="text-foreground">Alpaca Paper</span>
            <span className="text-muted-foreground"> · key </span>
            <span className="font-mono text-foreground">{maskApiKey(settings.apiKey)}</span>
            <span className="text-muted-foreground"> · last updated </span>
            <span className="text-foreground">{relTime(settings.updatedAt)}</span>
          </div>
        )}

        {editing && (
          <div className="space-y-3">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-muted-foreground uppercase tracking-wider">
                API Key {!hasCreds && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="PKxxxxxxxxxxxxxx"
                className="h-8 w-full max-w-[400px] rounded-md border bg-background px-2 text-xs font-mono outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-muted-foreground uppercase tracking-wider">
                API Secret {!hasCreds && <span className="text-red-500">*</span>}
              </label>
              <input
                type="password"
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                placeholder={hasCreds ? '**** keep existing' : 'Paste your secret'}
                className="h-8 w-full max-w-[400px] rounded-md border bg-background px-2 text-xs font-mono outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              {hasCreds && (
                <span className="text-[10px] text-muted-foreground">
                  Leave blank to keep the existing secret.
                </span>
              )}
            </div>


            {error && (
              <div className="text-xs text-red-500 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> {error}
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                onClick={() => void handleSave()}
                disabled={saving || !apiKey || (!hasCreds && !apiSecret)}
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                Save
              </Button>
              {hasCreds && (
                <Button variant="outline" size="sm" onClick={handleCancel} disabled={saving}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Toggle Switch (local copy — no external dep) ──

interface ToggleSwitchProps {
  checked: boolean;
  disabled?: boolean;
  loading?: boolean;
  onChange: (next: boolean) => void;
  ariaLabel?: string;
  size?: 'md' | 'lg';
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  disabled,
  loading,
  onChange,
  ariaLabel,
  size = 'md',
}) => {
  const dims = size === 'lg'
    ? { track: 'h-7 w-12', thumb: 'h-5 w-5', thumbOn: 'translate-x-5', thumbOff: 'translate-x-1' }
    : { track: 'h-5 w-9', thumb: 'h-4 w-4', thumbOn: 'translate-x-4', thumbOff: 'translate-x-0.5' };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex ${dims.track} items-center rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        checked ? 'bg-emerald-500' : 'bg-muted-foreground/30'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block ${dims.thumb} transform rounded-full bg-white shadow transition-transform ${
          checked ? dims.thumbOn : dims.thumbOff
        } flex items-center justify-center`}
      >
        {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </span>
    </button>
  );
};

export default PaperTradingSettingsPage;
